<template>
  <div class="campo-busca-lista-container mb-3">
    <label v-if="label && !hideLabel" :for="id" class="form-label" :class="{ 'is-required': isObrigatorio }">
      {{ label }}
      <i
        v-if="labelInfo"
        class="bi bi-info-circle text-primary ms-1"
        :title="labelInfo"
        style="cursor: help; font-size: 0.85em;"
      ></i>
    </label>

    <div class="input-group has-validation">
      <input
        :id="id"
        type="text"
        class="form-control"
        :class="{ 'is-invalid': !!errorMessage, 'input-readonly-clickable': !isDisabled }"
        v-model="textoVisivel"
        v-bind="$attrs"
        readonly
        placeholder="Selecione na lista..."
        @click="!isDisabled && abrirModal()"
      >
      <button
        class="btn btn-outline-secondary d-flex align-items-center"
        type="button"
        @click="abrirModal"
        :class="{ 'border-danger text-danger': !!errorMessage }"
        :disabled="isDisabled"
      >
        <i class="bi bi-list-ul"></i>
      </button>
      <button
        v-if="textoVisivel && !isDisabled"
        class="btn btn-outline-secondary d-flex align-items-center"
        type="button"
        @click="limparValor"
        title="Limpar seleção"
      >
        <i class="bi bi-x-lg"></i>
      </button>

      <div v-if="errorMessage" class="invalid-feedback">{{ errorMessage }}</div>
    </div>

    <!-- Dropdown fantasma com últimas seleções (só aparece quando o campo está vazio) -->
    <div v-if="ultimasSelecoes && ultimasSelecoes.length > 0 && !isDisabled && !textoVisivel && mostrarFantasma"
         class="ultimas-selecoes-fantasma">
      <div
        v-for="(item, idx) in ultimasSelecoes"
        :key="item.idListaClienteItem || item.idListaSistemaItem || idx"
        class="fantasma-item d-flex align-items-center px-3 py-2 w-100"
        @click="selecionarUltima(item)"
      >
        <i class="bi bi-clock-history text-secondary me-2" style="font-size: 0.75rem;"></i>
        <span class="fw-medium text-body-secondary">{{ item.textoLista }}</span>
        <small v-if="item.descricaoItem" class="text-body-tertiary ms-1">— {{ item.descricaoItem }}</small>
      </div>
    </div>

    <div v-if="descricao && !errorMessage" class="form-text mt-1 shadow-sm px-2 py-1 rounded bg-body-tertiary border border-secondary-subtle d-inline-block text-body-emphasis">
      <i class="bi bi-info-circle me-1 text-primary"></i> {{ descricao }}
    </div>

    <ModalDeBusca
      v-if="exibirModal && configModal"
      :configuracao="configModal"
      :itens-ja-selecionados="ultimoItemSelecionado ? [ultimoItemSelecionado] : []"
      @fechar="exibirModal = false"
      @item-selecionado="onItemSelecionado"
    />
  </div>
</template>

<script setup>
import { ref, watch, watchEffect, onMounted, computed, inject, useAttrs } from 'vue'
import { useMetadataListasStore } from '@/stores/metadataListas'
import { ST } from '@/constants/ST'
import ModalDeBusca from '../modal/ModalDeBusca.vue'

const attrs = useAttrs()
const metadataStore = useMetadataListasStore()

const props = defineProps({
  id: { type: String, required: true },
  label: { type: String, required: true },
  modelValue: { type: String, default: null },
  prefixo: { type: String, default: '' },
  options: { type: Array, default: null },
  required: { type: [Boolean, String, Number], default: false },
  hideLabel: { type: Boolean, default: false },
  error: { type: String, default: '' },
  contexto: { type: String, default: '' },
  tabela: { type: String, default: '' },
  labelInfo: { type: String, default: '' },
  /// <summary>Array opcional de objetos { idListaClienteItem, textoLista, descricaoItem }
  /// para exibir como dropdown fantasma de "últimas seleções".</summary>
  ultimasSelecoes: { type: Array, default: null }
})

const emit = defineEmits(['update:modelValue', 'selecionado'])

const textoVisivel = ref('')
const valorAnterior = ref('')
const descricao = ref('')
const exibirModal = ref(false)
/// <summary>Objeto completo do último item selecionado, repassado ao ModalDeBusca
/// via itensJaSelecionados para exibir o item no footer e destacá-lo no grid.</summary>
const ultimoItemSelecionado = ref(null)
/// <summary>Controla a exibição do dropdown fantasma com animação atrasada.</summary>
const mostrarFantasma = ref(false)
let _fantasmaTimer = null

const formErrors = inject('formErrors', ref({}))
const errorMessage = computed(() => props.error || formErrors.value[props.id] || '')

const isDisabled = computed(() => {
  return attrs.disabled === true || attrs.disabled === '' || attrs.disabled === 'disabled' || attrs.disabled === 'true'
})

const isObrigatorio = computed(() => {
  return props.required === true || props.required === 'true' || props.required === 'required' || props.required === ''
})

// ============================================================
// RESOLUÇÃO VIA DicionarioMetadadosListas
// ============================================================
const metadataTentouCarregar = ref(false)

onMounted(async () => {
  if (props.tabela) {
    await metadataStore.carregarMetadados()
  }
  metadataTentouCarregar.value = true
})

const mappingResolvido = computed(() => {
  if (!props.tabela || !props.id) return null
  return metadataStore.getMapping(props.tabela, props.id)
})

const prefixoResolvido = computed(() => {
  if (mappingResolvido.value) return mappingResolvido.value.codigoLista
  if (props.prefixo) return props.prefixo
  if (props.tabela && !props.prefixo && metadataTentouCarregar.value) {
    console.warn(
      `[CampoBuscaLista] Nenhum mapeamento encontrado para "${props.tabela}.${props.id}". ` +
      'Verifique o DicionarioMetadadosListas no banco.'
    )
  }
  return ''
})

const contextoResolvido = computed(() => {
  if (mappingResolvido.value) return mappingResolvido.value.tipoLista
  return props.contexto || 'Cliente'
})

const baseEndpoint = computed(() =>
  contextoResolvido.value?.toLowerCase() === 'sistema' ? 'ListasSistema' : 'ListasCliente'
)

const idFieldName = computed(() =>
  contextoResolvido.value?.toLowerCase() === 'sistema' ? 'idListaSistemaItem' : 'idListaClienteItem'
)

// ============================================================
// CARREGAMENTO AUTOMÁTICO DE OPTIONS (cache do metadata store)
// ============================================================
const optionsInternas = ref(null)
let _lastOptionsAsync = 0

watchEffect(async () => {
  // Se o pai forneceu options explicitamente (inclusive array vazio), usa-o e não busca do metadata
  if (props.options !== undefined && props.options !== null) {
    optionsInternas.value = Array.isArray(props.options) ? props.options : props.options
    return
  }
  if (!prefixoResolvido.value) {
    optionsInternas.value = null
    return
  }
  const currentCall = ++_lastOptionsAsync
  const lista = await metadataStore.getListaLookup(
    prefixoResolvido.value,
    contextoResolvido.value
  )
  // Só aplica se nenhum options foi passado depois que esta chamada iniciou
  // NOTA: props.options default é null, por isso verificamos null E undefined
  if (currentCall === _lastOptionsAsync && (props.options === undefined || props.options === null)) {
    optionsInternas.value = lista
  }
})

const optionsOrdenadas = computed(() => {
  const fonte = optionsInternas.value || props.options
  if (!fonte || !Array.isArray(fonte)) return fonte
  return [...fonte].sort((a, b) => {
    const seqA = a.sequenciaLista ?? a.SequenciaLista
    const seqB = b.sequenciaLista ?? b.SequenciaLista
    const hasA = seqA !== undefined && seqA !== null
    const hasB = seqB !== undefined && seqB !== null
    if (hasA && hasB) return seqA - seqB
    if (hasA) return -1
    if (hasB) return 1
    return 0
  })
})

const dadosLocaisAtivos = computed(() => {
  if (!Array.isArray(optionsOrdenadas.value)) return optionsOrdenadas.value
  return optionsOrdenadas.value.filter(opt => {
    if (typeof opt !== 'object' || opt === null) return true
    // A API pode retornar o campo em camelCase ('ativo') ou PascalCase ('Ativo')
    const statusAtivo = opt.Ativo !== undefined ? opt.Ativo : opt.ativo
    // Se não tem campo Ativo/ativo, não é item de lista -> passa livre
    if (statusAtivo === undefined) return true
    // Apenas ativos
    return statusAtivo === true
  })
})

const configModal = computed(() => {
  const prefixo = prefixoResolvido.value
  if (!prefixo) return null

  return {
    titulo: `Selecionar ${props.label}`,
    endpointApi: `${baseEndpoint.value}/itensBusca`,
    dadosLocais: dadosLocaisAtivos.value,
    idField: idFieldName.value,
    filtrosFixos: { prefixoCabecalho: prefixo, lstStatus: ST.Ativo },
    camposFiltro: [{ model: 'busca', label: 'Sigla ou Descrição', tipo: 'text' }],
    colunasGrid: [
      { header: 'Seq', dataField: 'sequenciaLista', width: '60px' },
      { header: 'Valor', dataField: 'textoLista', width: '30%' },
      { header: 'Descrição', dataField: 'descricaoItem', width: '70%' }
    ]
  }
})

const buscarPorId = () => {
  if (!props.modelValue) return

  const fonte = optionsInternas.value || props.options
  if (!fonte || !Array.isArray(fonte) || fonte.length === 0) return

  const modelStr = String(props.modelValue)

  // 1. Match exato por idFieldName
  const porId = fonte.find(opt =>
    String(opt[idFieldName.value]) === modelStr ||
    String(opt.idListaSistemaItem) === modelStr ||
    String(opt.idListaClienteItem) === modelStr ||
    String(opt.idListaSistemaItem) === modelStr ||
    String(opt.idListaClienteItem) === modelStr
  )
  if (porId) {
    ultimoItemSelecionado.value = porId
    textoVisivel.value = porId.textoLista
    valorAnterior.value = porId.textoLista
    descricao.value = porId.descricaoItem
    return
  }

  // 2. Fallback: busca por textoLista (case-insensitive)
  const porTexto = fonte.find(opt =>
    String(opt.textoLista).toLowerCase() === modelStr.toLowerCase()
  )
  if (porTexto) {
    ultimoItemSelecionado.value = porTexto
    textoVisivel.value = porTexto.textoLista
    valorAnterior.value = porTexto.textoLista
    descricao.value = porTexto.descricaoItem
    return
  }

  // 3. Fallback: match numérico direto
  const numVal = Number(props.modelValue)
  if (!isNaN(numVal)) {
    const porNumero = fonte.find(opt =>
      Number(opt[idFieldName.value]) === numVal ||
      Number(opt.idListaSistemaItem) === numVal ||
      Number(opt.idListaClienteItem) === numVal
    )
    if (porNumero) {
      ultimoItemSelecionado.value = porNumero
      textoVisivel.value = porNumero.textoLista
      valorAnterior.value = porNumero.textoLista
      descricao.value = porNumero.descricaoItem
      return
    }
  }

  // 4. Fallback: extrai sufixo numerico do modelValue (ex: "ABC_DEF_3" -> 3)
  const ultimoUnderscore = modelStr.lastIndexOf('_')
  if (ultimoUnderscore >= 0) {
    const suffixo = modelStr.substring(ultimoUnderscore + 1)
    const suffixNum = Number(suffixo)
    if (!isNaN(suffixNum)) {
      const porSufixo = fonte.find(opt =>
        Number(opt[idFieldName.value]) === suffixNum ||
        Number(opt.idListaSistemaItem) === suffixNum ||
        Number(opt.idListaClienteItem) === suffixNum
      )
      if (porSufixo) {
        ultimoItemSelecionado.value = porSufixo
        textoVisivel.value = porSufixo.textoLista
        valorAnterior.value = porSufixo.textoLista
        descricao.value = porSufixo.descricaoItem
        return
      }
    }
  }

  // 5. Fallback: tenta extrair ID numérico removendo o prefixo conhecido (ex: "UN_MED1" -> "1")
  if (prefixoResolvido.value && modelStr.startsWith(prefixoResolvido.value)) {
    const resto = modelStr.substring(prefixoResolvido.value.length)
    const restoNum = Number(resto)
    if (!isNaN(restoNum)) {
      const porPrefixo = fonte.find(opt =>
        Number(opt[idFieldName.value]) === restoNum ||
        Number(opt.idListaSistemaItem) === restoNum ||
        Number(opt.idListaClienteItem) === restoNum
      )
      if (porPrefixo) {
        ultimoItemSelecionado.value = porPrefixo
        textoVisivel.value = porPrefixo.textoLista
        valorAnterior.value = porPrefixo.textoLista
        descricao.value = porPrefixo.descricaoItem
        return
      }
    }
  }

  console.warn(
    `[CampoBuscaLista] Nao encontrou item para modelValue="${modelStr}".`,
    `idFieldName="${idFieldName.value}", prefixo="${prefixoResolvido.value}"`,
    `contexto="${contextoResolvido.value}", total opcoes=${fonte.length}`
  )
}

// --- WATCHES ---
const opcoesProntas = ref(false)

watch(optionsInternas, (opts) => {
  if (Array.isArray(opts) && opts.length > 0) {
    opcoesProntas.value = true
    if (props.modelValue) {
      buscarPorId()
    }
  } else if (Array.isArray(opts) && opts.length === 0) {
    console.warn(`[CampoBuscaLista:${props.id}] optionsInternas veio como array VAZIO!`)
  }
}, { immediate: true })

watch(() => props.options, (newOptions) => {
  if (newOptions && newOptions.length > 0 && props.modelValue) {
    buscarPorId()
  }
}, { deep: true })

watch(() => props.modelValue, (newVal) => {
  if (!newVal) {
    textoVisivel.value = ''; descricao.value = ''; valorAnterior.value = ''
    ultimoItemSelecionado.value = null
  } else if (newVal && opcoesProntas.value) {
    buscarPorId()
  } else if (newVal && !opcoesProntas.value) {
    console.warn(`[CampoBuscaLista:${props.id}] modelValue mudou mas opcoesProntas=false! Aguardando options carregarem...`)
  }
})

// Dispara a animação do dropdown fantasma com atraso quando as últimas seleções mudam
watch(() => props.ultimasSelecoes, (novas) => {
  if (_fantasmaTimer) { clearTimeout(_fantasmaTimer); _fantasmaTimer = null }
  if (novas && novas.length > 0 && !textoVisivel.value) {
    _fantasmaTimer = setTimeout(() => {
      mostrarFantasma.value = true
      _fantasmaTimer = null
    }, 400)
  } else {
    mostrarFantasma.value = false
  }
}, { deep: true })

// Garante que o fantasma suma imediatamente quando o campo é preenchido
watch(textoVisivel, (novo) => {
  if (novo) {
    if (_fantasmaTimer) { clearTimeout(_fantasmaTimer); _fantasmaTimer = null }
    mostrarFantasma.value = false
  }
})

const abrirModal = () => { exibirModal.value = true }

const limparValor = () => {
  textoVisivel.value = ''
  descricao.value = ''
  valorAnterior.value = ''
  ultimoItemSelecionado.value = null
  emit('update:modelValue', null)
}

const onItemSelecionado = (item) => {
  const idValor = item[idFieldName.value] ?? item.idListaSistemaItem ?? item.idListaClienteItem
  if (idValor === undefined || idValor === null) {
    console.error('[CampoBuscaLista] Não foi possível extrair idValor do item:', item)
    return
  }
  ultimoItemSelecionado.value = item
  emit('update:modelValue', idValor)
  textoVisivel.value = item.textoLista
  valorAnterior.value = item.textoLista
  descricao.value = item.descricaoItem
  emit('selecionado', item)
  exibirModal.value = false
}

const selecionarUltima = (item) => {
  onItemSelecionado(item)
}
</script>

<style scoped>
.form-label { font-weight: 400 !important; }
.form-label.is-required::after { content: " *"; color: #dc3545; font-weight: bold; }
.campo-busca-lista-container { display: flex; flex-direction: column; position: relative; }
.input-readonly-clickable {
  background-color: var(--bs-body-bg) !important;
  cursor: pointer;
}

/* Dropdown fantasma: ocupa toda a largura do container, semi-transparente */
.ultimas-selecoes-fantasma {
  width: 100%;
  opacity: 0.5;
  animation: fantasmaSlideDown 0.3s ease-out;
  margin-top: 0.1rem;
}
.ultimas-selecoes-fantasma .fantasma-item {
  width: 100%;
  background-color: var(--bs-tertiary-bg);
  border: 1px dashed var(--bs-border-color);
  border-radius: var(--bs-border-radius);
  cursor: pointer;
  font-size: 0.8rem;
  opacity: 0.55;
  transition: opacity 0.25s, background-color 0.2s, border-style 0.2s;
}
.ultimas-selecoes-fantasma .fantasma-item:hover {
  opacity: 0.9;
  background-color: var(--bs-secondary-bg);
  border-style: solid;
}

@keyframes fantasmaSlideDown {
  from {
    opacity: 0;
    max-height: 0;
    transform: translateY(-0.4rem);
  }
  to {
    opacity: 1;
    max-height: 5rem;
    transform: translateY(0);
  }
}
</style>
